import React from 'react';
import { Box, Typography, Button } from '@material-ui/core';
import { withStyles } from '@material-ui/styles';
import { ArticleCard, GridContainer, GridItem, SectionSeparator } from '../../components';
import PropTypes from 'prop-types';
import styles from './styles';
import { POSTS_BY_USER, POSTS_ABOUT_USER } from '../../lib/queries/posts';
import { useQuery } from '@apollo/react-hooks';

const LOAD_MORE_INCREMENT = 18;

const RelatedPostsGrid = (props) => {
    const { classes, profile, isElevatorStaff, mobile, renderLoading } = props;

    const { preferredUsername } = profile;
    const postTextPrefix = isElevatorStaff ? 'By' : 'About';
    const postQuery = isElevatorStaff ? POSTS_BY_USER : POSTS_ABOUT_USER;

    const { data, loading, networkStatus, error, refetch, fetchMore } = useQuery(
        postQuery,
        {
            notifyOnNetworkChange: true,
            variables: {
                preferredUsername,
                limit: LOAD_MORE_INCREMENT,
            },
        },
    );

    const isLoading = loading || networkStatus === 4;
    const isLoadingMore = networkStatus === 3;

    if (isLoading) return renderLoading();
    else if (error)
        return (
            <Box
                height={'70vh'}
                width={'100%'}
                display={'flex'}
                flexDirection={'column'}
                justifyContent={'center'}
                alignItems={'center'}
            >
                <Typography>
                    Error retrieving posts {postTextPrefix.toLowerCase()}{' '}
                    <b>{preferredUsername}</b>
                </Typography>
                <Button color={'primary'} onClick={() => refetch()}>
                    Retry
                </Button>
            </Box>
        );

    const posts = data && data.posts ? data.posts : [];

    const onLoadMore = () => {
        // avoid redundant fetch
        if (posts.length % LOAD_MORE_INCREMENT !== 0) return;

        fetchMore({
            variables: {
                preferredUsername,
                limit: LOAD_MORE_INCREMENT,
                skip: posts.length,
            },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev;
                const newPosts = [...prev.posts, ...fetchMoreResult.posts];

                return {
                    posts: newPosts,
                };
            },
        });
    };

    return (
        <>
            <SectionSeparator
                text={`Posts ${postTextPrefix} ${preferredUsername}`}
                paddingY={4}
                paddingX={mobile ? 2 : 0}
            />
            <Box paddingX={mobile ? 2 : 0} paddingBottom={4}>
                {posts.length < 1 && (
                    <Box
                        minHeight="200px"
                        display={'flex'}
                        flexDirection={'row'}
                        alignItems={'center'}
                        justifyContent={'center'}
                    >
                        <Typography
                            variant={'h5'}
                            color={'textSecondary'}
                            className={classes.emptyState}
                        >
                            No Posts
                        </Typography>
                    </Box>
                )}
                {posts.length > 0 && (
                    <GridContainer direction={'row'} spacing={3}>
                        {posts.map((postData, index) => (
                            <GridItem key={index} xs={12} sm={6} md={4}>
                                <ArticleCard postData={postData} />
                            </GridItem>
                        ))}
                        {posts.length % LOAD_MORE_INCREMENT === 0 && (
                            <GridItem xs={12}>
                                <Button
                                    variant={'outlined'}
                                    size={'large'}
                                    className={classes.loadMoreButton}
                                    onClick={onLoadMore}
                                    fullWidth
                                >
                                    Load More
                                </Button>
                            </GridItem>
                        )}
                    </GridContainer>
                )}
            </Box>
        </>
    );
};

RelatedPostsGrid.propTypes = {
    classes: PropTypes.object.isRequired,
    profile: PropTypes.object.isRequired,
    mobile: PropTypes.bool.isRequired,
    isElevatorStaff: PropTypes.bool.isRequired,
};

RelatedPostsGrid.defaultProps = {
    isElevatorStaff: false,
};

export default withStyles(styles)(RelatedPostsGrid);
